import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getRouteAccess,
  isAccessAllowed,
} from '../../constants/cmsAccess';

const normalizePermissionSource = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap(normalizePermissionSource);
  }

  if (typeof value === 'string') {
    const clean = value.trim().toLowerCase();
    return clean ? [clean] : [];
  }

  if (typeof value === 'object') {
    // Accept nested shapes such as { permissions: [...] }.
    if (value.permissions !== undefined) {
      return normalizePermissionSource(value.permissions);
    }

    // Also accept boolean maps:
    // { agreements_view: true, agreements_create: true }
    return Object.entries(value)
      .filter(([, enabled]) => enabled === true)
      .map(([key]) => String(key).trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
};

const normalizeList = (value) =>
  [...new Set(normalizePermissionSource(value))];

const RoleProtectedRoute = ({
  children,
  permissions,
  allOf,
  anyOf,
  redirectTo = '/company-dashboard',
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    userData,
    roleData,
    permissions: contextPermissions,
    loading,
    authError,
    isSuperAdmin,
  } = useAuth();

  const explicitRule = React.useMemo(() => {
    const hasExplicitRule =
      permissions !== undefined ||
      allOf !== undefined ||
      anyOf !== undefined;

    if (hasExplicitRule) {
      return {
        permissions:
          normalizeList(permissions),
        allOf:
          normalizeList(allOf),
        anyOf:
          normalizeList(anyOf),
      };
    }

    return getRouteAccess(
      location.pathname
    );
  }, [
    permissions,
    allOf,
    anyOf,
    location.pathname,
  ]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: 320,
          display: 'grid',
          placeItems: 'center',
          padding: 20,
          background: '#f8fafc',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            color: '#64748b',
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              margin: '0 auto 10px',
              border: '3px solid rgba(79,70,229,.12)',
              borderTopColor: '#4f46e5',
              borderRadius: '50%',
              animation: 'roleSpin .8s linear infinite',
            }}
          />
          Loading role permissions…

          <style>{`
            @keyframes roleSpin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // ProtectedRoute normally handles this first, but keeping this
  // guard defensive avoids a blank render when it is used alone.
  if (!user || !userData) {
    return (
      <div
        style={{
          minHeight: 320,
          display: 'grid',
          placeItems: 'center',
          color: '#64748b',
          fontSize: 11,
        }}
      >
        Authentication profile is unavailable.
      </div>
    );
  }

  /*
   * IMPORTANT:
   * The Agreement list, AuthContext and route guard must agree on the SAME
   * effective permission set. Different versions of this project have stored
   * role permissions in slightly different places, so merge every legitimate
   * source instead of trusting only contextPermissions.
   */
  const resolvedPermissions = React.useMemo(
    () => [
      ...new Set([
        ...normalizePermissionSource(contextPermissions),
        ...normalizePermissionSource(userData?.permissions),
        ...normalizePermissionSource(roleData?.permissions),
        ...normalizePermissionSource(userData?.role?.permissions),
        ...normalizePermissionSource(userData?.rolePermissions),
        ...normalizePermissionSource(userData?.access?.permissions),
      ]),
    ],
    [
      contextPermissions,
      userData?.permissions,
      roleData?.permissions,
      userData?.role?.permissions,
      userData?.rolePermissions,
      userData?.access?.permissions,
    ]
  );

  // Super Admin bypasses fine-grained role checks.
  // Company owner/admin roles can also use 'all' without being
  // misclassified as Super Admin by AuthContext.
  const allowed =
    isSuperAdmin ||
    resolvedPermissions.includes('all') ||
    isAccessAllowed(
      resolvedPermissions,
      explicitRule
    );

  if (!allowed) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[RoleProtectedRoute] Access denied', {
        pathname: location.pathname,
        required: explicitRule,
        resolvedPermissions,
        contextPermissions,
        userPermissions: userData?.permissions,
        rolePermissions:
          roleData?.permissions ||
          userData?.role?.permissions ||
          null,
      });
    }

    const required = [
      ...(explicitRule?.allOf || []),
      ...(explicitRule?.anyOf || []),
      ...(explicitRule?.permissions || []),
    ];

    return (
      <div
        style={{
          minHeight: '70vh',
          display: 'grid',
          placeItems: 'center',
          padding: 22,
          background:
            'linear-gradient(155deg,#f8fafc,#eef2ff)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            padding: 30,
            borderRadius: 24,
            textAlign: 'center',
            background: 'rgba(255,255,255,.94)',
            border: '1px solid rgba(255,255,255,.9)',
            boxShadow: '0 24px 60px rgba(15,23,42,.11)',
          }}
        >
          <div
            style={{
              width: 58,
              height: 58,
              margin: '0 auto',
              borderRadius: 18,
              display: 'grid',
              placeItems: 'center',
              background:
                'linear-gradient(135deg,#dc2626,#f97316)',
              color: '#fff',
              fontSize: 26,
            }}
          >
            🔒
          </div>

          <h2
            style={{
              margin: '14px 0 6px',
              color: '#0f172a',
            }}
          >
            Access Restricted
          </h2>

          <p
            style={{
              margin: 0,
              color: '#64748b',
              fontSize: 11.5,
              lineHeight: 1.55,
            }}
          >
            Your assigned role does not include permission for this page.
          </p>

          {required.length ? (
            <div
              style={{
                marginTop: 13,
                padding: 9,
                borderRadius: 11,
                background: 'rgba(220,38,38,.06)',
                color: '#b91c1c',
                fontSize: 9.3,
                fontWeight: 800,
                overflowWrap: 'anywhere',
              }}
            >
              Required: {required.join(', ')}
            </div>
          ) : null}

          {authError ? (
            <div
              style={{
                marginTop: 10,
                color: '#b45309',
                fontSize: 9.3,
              }}
            >
              {authError}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() =>
              navigate(
                redirectTo,
                { replace: true }
              )
            }
            style={{
              marginTop: 17,
              minHeight: 40,
              padding: '9px 15px',
              border: 0,
              borderRadius: 12,
              background:
                'linear-gradient(135deg,#4f46e5,#0891b2)',
              color: '#fff',
              fontWeight: 900,
              cursor: 'pointer',
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default RoleProtectedRoute;